import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, Bell, Search, CalendarDays, RotateCcw, Bot, UserRound } from "lucide-react";

const ACTION_LABELS = {
  approve: "Aprovação", reject: "Rejeição", credit_sale: "Venda a Crédito", payment: "Recebimento", deposit: "Depósito",
  withdrawal: "Levantamento", transfer: "Transferência", login: "Login", login_failed: "Login falhado", create: "Criação",
  update: "Alteração", delete: "Eliminação", cancel: "Anulação", pay: "Pagamento", status: "Estado", items: "Itens",
  remove_item: "Item removido", generate_cancel_code: "Código anulação", revoke_cancel_code: "Código revogado",
  cancel_invoice_denied: "Anulação recusada", open: "Abertura", close: "Fecho", credit: "Crédito",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

export default function AuditPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo, setDateTo] = useState(todayISO());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (moduleFilter !== "all") params.append("module", moduleFilter);
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (q.trim()) params.append("q", q.trim());
      const [lRes, aRes, mRes] = await Promise.all([
        axios.get(`${API_URL}/audit/logs?${params}`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/alerts`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/audit/modules`, { headers, withCredentials: true }),
      ]);
      setLogs(lRes.data);
      setAlerts(aRes.data.alerts);
      setModules(mRes.data);
    } catch { toast.error("Erro ao carregar auditoria"); }
    finally { setLoading(false); }
  }, [token, moduleFilter, sourceFilter, dateFrom, dateTo, q]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const reset = () => { setModuleFilter("all"); setSourceFilter("all"); setDateFrom(daysAgoISO(7)); setDateTo(todayISO()); setQ(""); };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Auditoria</h1>
        <p className="text-muted-foreground">Registo datado de todas as ações realizadas no sistema — quem fez, o quê, quando e com que resultado</p>
      </div>

      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 font-semibold">
            <Bell className="w-4 h-4 text-amber-500" /> Alertas Ativos ({alerts.length})
          </div>
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem alertas — está tudo em ordem ✓</p>
          ) : (
            <div className="space-y-1">
              {alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-sm" data-testid={`alert-${a.type}-${i}`}>
                  <AlertTriangle className={`w-4 h-4 ${a.severity === "danger" ? "text-red-500" : a.severity === "warning" ? "text-amber-500" : "text-blue-500"}`} />
                  {a.message}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3" data-testid="audit-filters">
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="w-full lg:w-56" data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os módulos</SelectItem>
            {modules.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full lg:w-44" data-testid="audit-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os registos</SelectItem>
            <SelectItem value="app">Detalhados</SelectItem>
            <SelectItem value="auto">Automáticos</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-muted-foreground shrink-0" />
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="audit-date-from" />
          <span className="text-muted-foreground text-sm">até</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="audit-date-to" />
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar por utilizador, documento, detalhe..." className="pl-9" data-testid="audit-search" />
        </div>
        <Button variant="ghost" size="sm" onClick={reset} className="gap-1.5" data-testid="audit-reset-filters"><RotateCcw className="w-3.5 h-3.5" /> Limpar</Button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y max-h-[640px] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground bg-muted/40 sticky top-0 z-10">
            <span data-testid="audit-count">{logs.length} registo(s)</span>
            <span>{dateFrom} → {dateTo}</span>
          </div>
          {loading ? <p className="p-6 text-muted-foreground">A carregar...</p> :
            logs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
                Sem registos de auditoria para os filtros escolhidos
              </div>
            ) : logs.map((log) => (
              <div key={log.log_id} className="flex items-start justify-between p-3 text-sm gap-3" data-testid={`audit-row-${log.log_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium break-words">{log.details || `${log.action} em ${log.entity}`}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                    {log.source === "auto" ? <Bot className="w-3 h-3" /> : <UserRound className="w-3 h-3" />}
                    <span className="font-medium text-foreground/80">{log.user_name || "Sistema"}</span>
                    <span>·</span>
                    <span className="font-mono" data-testid={`audit-date-${log.log_id}`}>{formatDate(log.created_at)}</span>
                    {log.ip && <span>· IP {log.ip}</span>}
                    {log.result && log.result !== "sucesso" && <span className="text-red-500 font-semibold">· {log.result}</span>}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline">{ACTION_LABELS[log.action] || log.action}</Badge>
                  {(log.module || log.entity) && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{modules.find((m) => m.value.split(",").includes(log.module || log.entity))?.label || log.module || log.entity}</span>}
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    </div>
  );
}
